import { useCallback, useEffect, useMemo, useState } from 'react';
import { getIntegratedDclAssessments } from '../api/integratedDclApi';

export const GALLAGHER_DCL_TENANT_ID = 'f9471bc0-c3b6-4f52-a0cd-ec6a328e54bb';
export const integratedDclEnabled = (tenantId, insuranceEnabled) => tenantId === GALLAGHER_DCL_TENANT_ID && insuranceEnabled === true;

// Identity and policy results remain server-owned. A tenant switch never reuses a prior map.
export default function useIntegratedDcl({ tenantId, insuranceEnabled, companyIds = [], revision, isDistributor = false, requesterId, identityReady = false }) {
  const enabled = Boolean(requesterId) && identityReady === true && isDistributor !== true && integratedDclEnabled(tenantId, insuranceEnabled);
  const idsKey = JSON.stringify([...new Set(companyIds.filter(Boolean))].sort());
  const scopeKey = JSON.stringify([requesterId, tenantId, enabled, idsKey]);
  const [retry, setRetry] = useState(0);
  const [state, setState] = useState({ scopeKey: null, assessments: {}, loading: false, error: '' });
  const reload = useCallback(() => setRetry(value => value + 1), []);
  useEffect(() => {
    if (!enabled) return undefined;
    const refresh = () => { if (document.visibilityState !== 'hidden') reload(); };
    const timer = window.setInterval(refresh, 60000);
    window.addEventListener('focus', refresh);
    return () => { window.clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [enabled, reload]);
  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;
    const ids = JSON.parse(idsKey);
    setState({ scopeKey, assessments: {}, loading: ids.length > 0, error: '' });
    const load = async () => {
      const assessments = {};
      try {
        // Bounded sequential batches avoid a request for every row or a giant query URL.
        for (let offset = 0; offset < ids.length; offset += 50) {
          const result = await getIntegratedDclAssessments(tenantId, ids.slice(offset, offset + 50));
          if (cancelled) return;
          const rows = Array.isArray(result) ? result : result?.assessments || [];
          for (const row of rows) {
            if (row?.tenantId !== tenantId || !ids.includes(row.companyId)) throw new Error('Insurance assessment scope could not be verified.');
            assessments[row.companyId] = row;
          }
        }
        if (!cancelled) setState({ scopeKey, assessments, loading: false, error: '' });
      } catch (error) {
        if (!cancelled) setState({ scopeKey, assessments: {}, loading: false, error: error?.response?.data?.message || error.message || 'Insurance assessments are unavailable.' });
      }
    };
    load();
    return () => { cancelled = true; };
  }, [tenantId, enabled, idsKey, scopeKey, revision, retry]);
  return useMemo(() => ({ enabled, reload, ...(enabled && state.scopeKey === scopeKey ? state : { assessments: {}, loading: enabled && companyIds.length > 0, error: '' }) }), [enabled, reload, state, scopeKey, companyIds.length]);
}
